import { useState } from "react";
import { MiBox, MiList, MiMenu, MiModalConfirm } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  ICONS_WEB,
  MODAL_TITLES,
  PAGINATION,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { formatDate } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box, useTheme } from "@mui/material";
import { AddSeriesModal } from "./AddPlanSeriesModal/AddSeriesModal";
import { AddSeriesGroupModal } from "./AddSeriesGroupModal/AddSeriesGroupModal";
import { usePlanSeriesViewModel } from "./usePlanSeriesViewModel";

export function PlanSeries({
  stateManager,
  debouncedSearch,
  openSeriesGroupModal,
  openSeriesModal,
  setOpenSeriesGroupModal,
  setOpenSeriesModal,
}) {
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openDeleteGroupModal, setOpenDeleteGroupModal] = useState(false);

  const {
    navigate,
    params: { accountId, tab },
  } = useRouter();
  const {
    isLoading,
    refresh,
    seriesGroup,
    getSeriesGroup,
    handleDeleteSeries,
    setRefresh,
    handleDeleteSeriesGroup,
    handleSetSeriesGroup,
    selectedSeries,
    setSelectedSeries,
    selectedSeriesGroup,
    setSelectedSeriesGroup,
  } = usePlanSeriesViewModel({
    stateManager,
    accountId,
  });
  const theme = useTheme();

  const sections = [
    {
      fieldset: [
        {
          label: "Name",
          field: "name",
        },
        {
          label: "Spec Level",
          field: "specLevel",
        },
        {
          label: "Created Date",
          field: "createdOn",
          renderValue: ({ createdOn }) =>
            formatDate(createdOn, "ddd, MMMM D, YYYY"),
        },
      ],
    },
  ];

  const handleNavigate = (item) => {
    navigate(`/estimating/old-bids/${accountId}/${tab}/${item.id}/all-bids`);
  };

  const handleOpenDeleteModal = (item) => {
    setSelectedSeries(item);
    setOpenDeleteModal(true);
  };

  const handleConfirmDeleteModal = () => {
    handleDeleteSeries(selectedSeries);
    setOpenDeleteModal(false);
  };

  const handleOpenGroupDeleteModal = (item) => {
    setSelectedSeriesGroup(item);
    setOpenDeleteGroupModal(true);
  };

  const handleConfirmGroupDeleteModal = () => {
    handleDeleteSeriesGroup(selectedSeries);
    setOpenDeleteGroupModal(false);
  };

  const renderItemActions = (item) => (
    <Box onClick={(e) => e.stopPropagation()}>
      <MiMenu
        data={[
          {
            label: BUTTON_LABELS.EDIT,
            icon: mdi[ICONS_WEB.PENCIL_OUTLINE],
            action: () => {
              setSelectedSeries(item);
              setOpenSeriesModal(true);
            },
          },
          {
            label: BUTTON_LABELS.DELETE,
            icon: mdi[ICONS_WEB.TRASH_CAN],
            action: () => handleOpenDeleteModal(item),
            color: theme.palette.red.main,
          },
        ]}
      />
    </Box>
  );

  const renderGroupActions = (item) => {
    if (!item.seriesGroupId) {
      return null;
    }

    return (
      <Box onClick={(e) => e.stopPropagation()}>
        <MiMenu
          data={[
            {
              label: BUTTON_LABELS.EDIT,
              icon: mdi[ICONS_WEB.PENCIL_OUTLINE],
              action: () => {
                const seriesGroupItem = {
                  id: item.seriesGroupId,
                  name: item.groupName,
                  accountId,
                  modifiedOn: item.groupModifiedOn,
                };
                setSelectedSeriesGroup(seriesGroupItem);
                setOpenSeriesGroupModal(true);
              },
            },
            {
              label: BUTTON_LABELS.DELETE,
              icon: mdi[ICONS_WEB.TRASH_CAN],
              action: () => handleOpenGroupDeleteModal(item),
              color: theme.palette.red.main,
            },
          ]}
        />
      </Box>
    );
  };

  const filteredSeriesGroup = debouncedSearch
    ? seriesGroup.filter((item) =>
        item.name?.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
    : seriesGroup;

  return (
    <MiBox>
      <MiList
        viewType="card"
        showRecent
        data={filteredSeriesGroup}
        setData={handleSetSeriesGroup}
        dataProvider={getSeriesGroup}
        paginationMode={PAGINATION.CLIENT}
        refresh={refresh}
        loading={isLoading}
        groupedMode
        groupedCriteria={(item) => item.groupName}
        showGroupCount
        pinGroupKeyToTop="Unassigned"
        cardProps={{
          sections,
          renderItemActions,
          onClick: (item) => handleNavigate(item),
          renderGroupActions,
        }}
        emptyListProps={{
          text: "No Series found",
        }}
        data-testid="plan-series-list"
      />
      <MiModalConfirm
        title={MODAL_TITLES.DELETE}
        open={openDeleteModal || openDeleteGroupModal}
        textPositive={BUTTON_LABELS.DELETE}
        buttonPositiveColor={BUTTON_COLORS.RED}
        titleIcon={{
          path: mdi[ICONS_WEB.TRASH_CAN],
          color: theme.palette.red.main,
        }}
        handleNegative={() => {
          if (openDeleteModal) {
            setOpenDeleteModal(false);
            setSelectedSeries(null);
          } else {
            setOpenDeleteGroupModal(false);
            setSelectedSeriesGroup(null);
          }
        }}
        handlePositive={() =>
          openDeleteModal
            ? handleConfirmDeleteModal()
            : handleConfirmGroupDeleteModal()
        }
        description={`You are about to delete ${
          openDeleteModal
            ? `${selectedSeries.name} series`
            : `${selectedSeriesGroup?.groupName} series group`
        }. Are you sure you would like to continue?`}
      />
      <AddSeriesModal
        open={openSeriesModal}
        closeModal={() => setOpenSeriesModal(false)}
        selectedSeries={selectedSeries}
        setSelectedSeries={setSelectedSeries}
        setRefresh={setRefresh}
        accountId={accountId}
      />
      <AddSeriesGroupModal
        open={openSeriesGroupModal}
        closeModal={() => setOpenSeriesGroupModal(false)}
        selectedSeriesGroup={selectedSeriesGroup}
        setSelectedSeriesGroup={setSelectedSeriesGroup}
        setRefresh={setRefresh}
        accountId={accountId}
      />
    </MiBox>
  );
}
